import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import MetodoPagoField from "../shared/MetodoPagoField";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";
import { Badge } from "../badge";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { PlusIcon, TrashIcon, PencilSquareIcon } from "@heroicons/react/20/solid";
import DialogoConfirmacion, { Resumen, Fila } from "../shared/DialogoConfirmacion";
import {
  formatCalendarDate,
  calendarDayAnchor,
  todayInputValue,
  getMonthRange,
  getYearRange,
  parseDateInputToTimestamp,
} from "../../utils/dates";
import { ACCENT, MONTHS_ES, formatMXN, defaultMonth } from "./shared";
import { CATEGORIAS_COSTO } from "../../utils/costos";
import { esDescuento } from "../../utils/catalogo";
import {
  asientosDelLedger,
  movimientosDeCaja,
  resumenLedger,
  ETIQUETAS_ASIENTO,
} from "../../utils/ledger";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import CuentasRecurrentes, { PropuestasRecurrentes } from "./CuentasRecurrentes";

const emptyGasto = {
  fecha: "",
  monto: "",
  categoria: "Renta",
  concepto: "",
  estado: "pagado",
  metodo: "",
  notas: "",
  respaldo: "",
};

// Un retiro no es un gasto (FRA-343): es repartición de utilidades y vive en
// venues/{id}/retiros. Solo pide lo mínimo — no tiene categoría, ni estado
// (siempre salió), ni evento al cual colgarse.
const emptyRetiro = {
  fecha: "",
  monto: "",
  concepto: "",
  metodo: "",
};

// Balance general (FRA-344): la fusión de Pagos, Reembolsos y Costos en una
// sola lectura, con el vocabulario correcto — Ingresos y Egresos — y arriba
// la cifra que el venue necesita antes que cualquier otra: cuánto hay en la
// cuenta y cuánto de eso está comprometido en depósitos. Tener $180,000 con
// $40,000 en custodia es tener $140,000.
//
// El neto del período solo cuenta dinero que de verdad se movió: los costos
// entran por su fecha de pago (FRA-342); lo por pagar vive en Por pagar. Los
// depósitos no entran al neto — no son tuyos — pero sí al saldo y al
// comprometido del encabezado.
const CuentasBalance = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedVenueId,
    payments,
    paymentsLoading,
    refunds,
    refundsLoading,
    costos,
    costosLoading,
    retiros,
    retirosLoading,
    clienteDepositos,
    clienteDepositosLoading,
    loadProveedorDepositos,
    events,
    proveedores,
    proveedoresLoading,
    conceptos,
    calendarTimezone,
    metodosPagoConDefaults: metodosPago,
  } = useVenue();
  const { userInfo, user } = useAuth();
  // Inversionista (P-FRA-13): se lee como estado de cuenta, sin capturar.
  const soloLectura = esSoloLectura(userInfo);

  const selectedMonth = searchParams.get("month") || defaultMonth();
  const vista = searchParams.get("vista") || "mensual";
  const [selectedYear, selectedMonthNum] = selectedMonth.split("-").map(Number);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => next.set(k, v));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyGasto);
  const [saving, setSaving] = useState(false);
  const [masDetalles, setMasDetalles] = useState(false);
  // Gasto general que se está editando (null = el formulario da de alta).
  const [editandoGasto, setEditandoGasto] = useState(null);
  const [showRetiro, setShowRetiro] = useState(false);
  const [retiroForm, setRetiroForm] = useState(emptyRetiro);
  const [savingRetiro, setSavingRetiro] = useState(false);

  const currentYear = new Date().getFullYear();
  const YEARS = [];
  for (let y = 2022; y <= currentYear + 1; y++) YEARS.push(y);

  // Depósitos de proveedor: snapshot al abrir (no viven suscritos).
  const [provDepositos, setProvDepositos] = useState(null);
  useEffect(() => {
    let vivo = true;
    loadProveedorDepositos()
      .then((deps) => { if (vivo) setProvDepositos(deps); })
      .catch((err) => {
        console.error("loadProveedorDepositos error:", err);
        if (vivo) setProvDepositos([]);
      });
    return () => { vivo = false; };
  }, [loadProveedorDepositos]);

  const eventsById = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => { map[e.id] = e; });
    return map;
  }, [events]);

  // El ledger completo una sola vez; de ahí salen el encabezado (histórico
  // completo) y las secciones (filtradas al período).
  const asientos = useMemo(
    () =>
      asientosDelLedger({
        payments,
        refunds,
        costos,
        depositos: [...(clienteDepositos || []), ...(provDepositos || [])],
        retiros,
      }),
    [payments, refunds, costos, clienteDepositos, provDepositos, retiros]
  );

  const encabezado = useMemo(() => resumenLedger(asientos), [asientos]);

  const { from, to } = vista === "anual"
    ? getYearRange(selectedYear)
    : getMonthRange(selectedMonth);

  const delPeriodo = useMemo(() => {
    const desde = calendarDayAnchor(from, calendarTimezone)?.getTime() || 0;
    const hasta = calendarDayAnchor(to, calendarTimezone)?.getTime() || Infinity;
    return movimientosDeCaja(asientos)
      .filter((a) => !a.tipo.startsWith("deposito_"))
      .map((a) => ({
        ...a,
        _ms: calendarDayAnchor(a.fecha, calendarTimezone)?.getTime() || 0,
        _tie: a.createdAt?.seconds || 0,
      }))
      .filter((a) => a._ms >= desde && a._ms <= hasta)
      .sort((a, b) => b._ms - a._ms || b._tie - a._tie);
  }, [asientos, from, to, calendarTimezone]);

  const ingresos = useMemo(
    () => delPeriodo.filter((a) => a.tipo === "pago" || a.tipo === "reembolso"),
    [delPeriodo]
  );
  const egresos = useMemo(
    () =>
      // La devolución de un proveedor (saldo a favor de un evento cancelado
      // que regresó) es reversión de gasto: entra a caja y baja los egresos.
      delPeriodo.filter(
        (a) =>
          a.tipo === "gasto" ||
          a.tipo === "comision_planner" ||
          a.tipo === "retiro" ||
          a.tipo === "devolucion_proveedor"
      ),
    [delPeriodo]
  );

  const totalIngresos = ingresos.reduce((s, a) => s + a.caja, 0);
  const totalEgresos = egresos.reduce((s, a) => s + -a.caja, 0);
  const neto = totalIngresos - totalEgresos;

  // ── Gasto general: esta es su única superficie de captura ──
  const proveedoresActivos = useMemo(
    () =>
      (proveedores || [])
        .filter((p) => (p.status || "activo") === "activo" && !p.archivedAt)
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")),
    [proveedores]
  );
  // Cualquier servicio del catálogo, no solo los que llevan piezas: ligar el
  // gasto es lo que después contesta "¿cuánto me ha costado esto?" y
  // alimenta la recuperación de la inversión.
  const recursosPropios = useMemo(
    () =>
      (conceptos || [])
        .filter((c) => !c.archivedAt && !esDescuento(c))
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")),
    [conceptos]
  );

  // «YYYY-MM-DD» de un día guardado, para el <input type="date">.
  const fechaInputDe = (val) => {
    const anchor = calendarDayAnchor(val, calendarTimezone);
    return anchor
      ? `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, "0")}-${String(
          anchor.getUTCDate()
        ).padStart(2, "0")}`
      : "";
  };

  // Un gasto general se corrige donde nació: el mismo formulario del alta,
  // con sus datos. Siendo pagado, la fecha que se edita es la de PAGO (la
  // que manda en Balance); la fecha del costo —la de su periodo, en un
  // recurrente— se conserva. Pasarlo a «Por pagar» lo regresa a pendiente.
  const abrirEdicionGasto = (costo) => {
    setForm({
      fecha: fechaInputDe(costo.fechaPago || costo.fecha) || todayInputValue(),
      monto: String(Number(costo.monto) || ""),
      categoria: costo.categoria || "Otro",
      concepto: costo.concepto || "",
      estado: costo.estado || "por_pagar",
      metodo: costo.metodo || "",
      notas: costo.notas || "",
      respaldo: costo.proveedorId
        ? `proveedor:${costo.proveedorId}`
        : costo.conceptoId
        ? `recurso:${costo.conceptoId}`
        : "",
    });
    setEditandoGasto(costo);
    setMasDetalles(true);
    setShowRetiro(false);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cerrarFormGasto = () => {
    setShowForm(false);
    setEditandoGasto(null);
    setMasDetalles(false);
    setForm(emptyGasto);
  };

  const handleAddGasto = async () => {
    if (!selectedVenueId) return;
    if (!form.fecha) {
      showAlert("Por favor ingresa la fecha del gasto.", "error");
      return;
    }
    const monto = Number(normalizeMoneyInput(form.monto));
    if (!monto || isNaN(monto) || monto <= 0) {
      showAlert("Por favor ingresa un monto válido mayor a 0.", "error");
      return;
    }
    if (!form.concepto.trim()) {
      showAlert("Por favor describe el concepto del gasto.", "error");
      return;
    }
    const fecha = parseDateInputToTimestamp(form.fecha, calendarTimezone);
    if (!fecha) {
      showAlert("Fecha inválida.", "error");
      return;
    }
    const proveedorId = form.respaldo.startsWith("proveedor:")
      ? form.respaldo.slice("proveedor:".length)
      : null;
    const recursoId = form.respaldo.startsWith("recurso:")
      ? form.respaldo.slice("recurso:".length)
      : null;
    const proveedor = proveedorId
      ? proveedoresActivos.find((p) => p.id === proveedorId)
      : null;
    const recurso = recursoId
      ? recursosPropios.find((r) => r.id === recursoId)
      : null;
    if (editandoGasto) {
      await guardarEdicionGasto({ fecha, monto, proveedorId, recursoId, proveedor, recurso });
      return;
    }
    setSaving(true);
    try {
      const docRef = await addDoc(
        collection(db, "venues", selectedVenueId, "costos"),
        {
          eventId: null, // gasto general del venue
          concepto: form.concepto.trim(),
          categoria: form.categoria,
          proveedorId: proveedor?.id || null,
          proveedorNombre: proveedor?.nombre || null,
          conceptoId: recurso?.id || null,
          conceptoNombre: recurso?.nombre || null,
          monto,
          montoOriginal: null,
          fecha,
          estado: form.estado,
          fechaPago: form.estado === "pagado" ? fecha : null,
          origen: "manual",
          metodo: form.metodo.trim() || null,
          notas: form.notas.trim() || null,
          createdAt: serverTimestamp(),
          createdBy: user?.uid || null,
          updatedAt: null,
          updatedBy: null,
        }
      );
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.COSTO_CREATE,
          entity: "costo",
          entityId: docRef.id,
          entityName: form.concepto.trim(),
          details: { monto, categoria: form.categoria, general: true },
        },
        userInfo,
        user
      );
      cerrarFormGasto();
      showAlert("Gasto registrado", "success");
    } catch (err) {
      showAlert(`Error al registrar el gasto. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const guardarEdicionGasto = async ({ fecha, monto, proveedorId, recursoId, proveedor, recurso }) => {
    const costo = editandoGasto;
    // Un proveedor o servicio ya archivado no está en las listas: si el
    // respaldo no se tocó, se conserva tal cual estaba.
    const respaldoIgual = proveedorId
      ? proveedorId === costo.proveedorId
      : recursoId
      ? recursoId === costo.conceptoId
      : !costo.proveedorId && !costo.conceptoId;
    const pagado = form.estado === "pagado";
    const cambios = {
      concepto: form.concepto.trim(),
      categoria: form.categoria,
      monto,
      estado: form.estado,
      fechaPago: pagado ? fecha : null,
      metodo: pagado ? form.metodo.trim() || null : costo.metodo || null,
      notas: form.notas.trim() || null,
      ...(respaldoIgual
        ? {}
        : {
            proveedorId: proveedor?.id || null,
            proveedorNombre: proveedor?.nombre || null,
            conceptoId: recurso?.id || null,
            conceptoNombre: recurso?.nombre || null,
          }),
      updatedAt: serverTimestamp(),
      updatedBy: user?.uid || null,
    };
    setSaving(true);
    try {
      await updateDoc(doc(db, "venues", selectedVenueId, "costos", costo.id), cambios);
      const montoAntes = Number(costo.monto) || 0;
      logBitacora(
        selectedVenueId,
        {
          action: pagado ? BITACORA_ACTIONS.COSTO_EDIT : BITACORA_ACTIONS.COSTO_TOGGLE_ESTADO,
          entity: "costo",
          entityId: costo.id,
          entityName: cambios.concepto,
          details: {
            general: true,
            ...(montoAntes !== monto ? { oldMonto: montoAntes, newMonto: monto } : { monto }),
            ...(pagado ? {} : { estado: "por_pagar" }),
            ...(cambios.metodo ? { metodo: cambios.metodo } : {}),
          },
        },
        userInfo,
        user
      );
      cerrarFormGasto();
      showAlert(pagado ? "Gasto actualizado" : "El gasto volvió a Por pagar", "success");
    } catch (err) {
      showAlert(`No se pudo guardar el gasto. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  // Confirmación antes de borrar un movimiento: { tipo, item } con tipo
  // "eliminar_gasto" | "eliminar_retiro".
  const [confirmacion, setConfirmacion] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const ejecutarConfirmacion = async () => {
    if (!confirmacion) return;
    setConfirmando(true);
    try {
      if (confirmacion.tipo === "eliminar_gasto") await borrarGasto(confirmacion.item);
      else if (confirmacion.tipo === "eliminar_retiro") await borrarRetiro(confirmacion.item);
    } finally {
      setConfirmando(false);
      setConfirmacion(null);
    }
  };

  const borrarGasto = async (costo) => {
    try {
      await deleteDoc(doc(db, "venues", selectedVenueId, "costos", costo.id));
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.COSTO_DELETE,
          entity: "costo",
          entityId: costo.id,
          entityName: costo.concepto || "",
          details: { monto: Number(costo.monto) || 0, general: true },
        },
        userInfo,
        user
      );
      showAlert("Gasto eliminado", "success");
    } catch (err) {
      showAlert(`No se pudo eliminar. [${err.message}]`, "error");
    }
  };

  // ── Retiro: su única superficie de captura ──
  const handleAddRetiro = async () => {
    if (!selectedVenueId) return;
    if (!retiroForm.fecha) {
      showAlert("Por favor ingresa la fecha del retiro.", "error");
      return;
    }
    const monto = Number(normalizeMoneyInput(retiroForm.monto));
    if (!monto || isNaN(monto) || monto <= 0) {
      showAlert("Por favor ingresa un monto válido mayor a 0.", "error");
      return;
    }
    const fecha = parseDateInputToTimestamp(retiroForm.fecha, calendarTimezone);
    if (!fecha) {
      showAlert("Fecha inválida.", "error");
      return;
    }
    setSavingRetiro(true);
    try {
      const docRef = await addDoc(collection(db, "venues", selectedVenueId, "retiros"), {
        monto,
        fecha,
        concepto: retiroForm.concepto.trim() || null,
        metodo: retiroForm.metodo.trim() || null,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.RETIRO_CREATE,
          entity: "retiro",
          entityId: docRef.id,
          entityName: retiroForm.concepto.trim() || "Retiro",
          details: { monto },
        },
        userInfo,
        user
      );
      setRetiroForm(emptyRetiro);
      setShowRetiro(false);
      showAlert("Retiro registrado", "success");
    } catch (err) {
      showAlert(`Error al registrar el retiro. [${err.message}]`, "error");
    } finally {
      setSavingRetiro(false);
    }
  };

  const borrarRetiro = async (retiro) => {
    try {
      await deleteDoc(doc(db, "venues", selectedVenueId, "retiros", retiro.id));
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.RETIRO_DELETE,
          entity: "retiro",
          entityId: retiro.id,
          entityName: retiro.concepto || "Retiro",
          details: { monto: Number(retiro.monto) || 0 },
        },
        userInfo,
        user
      );
      showAlert("Retiro eliminado", "success");
    } catch (err) {
      showAlert(`No se pudo eliminar. [${err.message}]`, "error");
    }
  };

  const inputClass =
    "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

  // proveedoresLoading: sin él, loadProveedorDepositos devuelve [] antes de
  // que el directorio cargue y el comprometido se pinta en cero un instante.
  const loading =
    paymentsLoading ||
    refundsLoading ||
    costosLoading ||
    retirosLoading ||
    clienteDepositosLoading ||
    proveedoresLoading ||
    provDepositos === null;

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Cargando...</div>;
  }

  const CeldaEvento = ({ asiento }) => {
    const ev = asiento.eventId ? eventsById[asiento.eventId] : null;
    if (ev) {
      return (
        <button
          type="button"
          onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
          className="text-sm hover:underline"
          style={{ color: ACCENT }}
        >
          {ev.nombre || "Sin nombre"}
        </button>
      );
    }
    return (
      <span className="text-xs text-zinc-400">
        {asiento.eventId ? "Evento no encontrado" : "General"}
      </span>
    );
  };

  // Cada movimiento se corrige donde nació: los gastos generales y los
  // retiros se capturan aquí y aquí se eliminan; los costos de evento
  // (comisiones y devoluciones incluidas) se corrigen desde su evento.
  // Deshacer un pago no vive aquí: un gasto general mal marcado se elimina y
  // se recaptura (un recurrente se vuelve a proponer solo).
  const corregibleAqui = (a) =>
    a.tipo === "retiro" || (a.tipo !== "pago" && !!a.origenDoc && !a.origenDoc.eventId);

  // La columna de acciones solo existe si algún renglón la usa: vacía, deja
  // un hueco a la derecha del monto que la tabla de ingresos no tiene.
  const Tabla = ({ lista, egreso = false }) => {
    const conAcciones = egreso && !soloLectura && lista.some(corregibleAqui);
    return (
    <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-700/60">
      <table className="min-w-full text-sm">
        <thead className="bg-zinc-50 dark:bg-zinc-800/40">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">{egreso ? "Fecha de pago" : "Fecha"}</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Tipo</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Concepto</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Método</th>
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Evento</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Monto</th>
            {conAcciones && <th className="w-px px-2 py-2.5" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
          {lista.map((a) => {
            const costo = egreso ? a.origenDoc : null;
            return (
              <tr key={a.id}>
                <td className="px-4 py-2.5 whitespace-nowrap text-zinc-500 w-32">
                  {formatCalendarDate(a.fecha, calendarTimezone, { style: "short" }) || "—"}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <Badge
                    color={
                      a.tipo === "pago"
                        ? "emerald"
                        : a.tipo === "reembolso"
                        ? "red"
                        : a.tipo === "retiro"
                        ? "zinc"
                        : a.tipo === "comision_planner"
                        ? "indigo"
                        : a.tipo === "devolucion_proveedor"
                        ? "teal"
                        : "amber"
                    }
                  >
                    {ETIQUETAS_ASIENTO[a.tipo]}
                  </Badge>
                </td>
                <td className="px-4 py-2.5">
                  {a.proveedorId ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/proveedores/${a.proveedorId}`)}
                      className="text-sm hover:underline text-left"
                      style={{ color: ACCENT }}
                    >
                      {a.concepto}
                    </button>
                  ) : (
                    <div className="text-zinc-900 dark:text-white">{a.concepto}</div>
                  )}
                  {a.detalle && (
                    <div className="text-xs text-zinc-400">
                      {a.detalle}
                      {a.origenDoc?.origen === "recurrente" && " · recurrente"}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-zinc-600 dark:text-zinc-300">
                  {a.metodo || <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  <CeldaEvento asiento={a} />
                </td>
                <td
                  className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${
                    a.caja >= 0 ? "text-emerald-600" : "text-red-600"
                  }`}
                >
                  {a.caja >= 0 ? "+" : "−"}{formatMXN(Math.abs(a.caja))}
                </td>
                {conAcciones && (
                  <td className="w-px px-2 py-2.5 text-right whitespace-nowrap">
                    {corregibleAqui(a) && a.tipo !== "retiro" && (
                      <button
                        type="button"
                        onClick={() => abrirEdicionGasto(costo)}
                        className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1"
                        title="Editar gasto"
                      >
                        <PencilSquareIcon className="h-4 w-4 inline" />
                      </button>
                    )}
                    {corregibleAqui(a) && (
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmacion(
                            a.tipo === "retiro"
                              ? { tipo: "eliminar_retiro", item: a.origenDoc }
                              : { tipo: "eliminar_gasto", item: costo }
                          )
                        }
                        className="text-zinc-400 hover:text-red-500 p-1"
                        title={a.tipo === "retiro" ? "Eliminar retiro" : "Eliminar gasto"}
                      >
                        <TrashIcon className="h-4 w-4 inline" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    );
  };

  return (
    <div className="p-5">
      {/* Confirmar antes de borrar un movimiento */}
      <DialogoConfirmacion
        open={!!confirmacion && !soloLectura}
        onClose={() => setConfirmacion(null)}
        saving={confirmando}
        icono={TrashIcon}
        titulo={confirmacion?.tipo === "eliminar_retiro" ? "Eliminar retiro" : "Eliminar gasto"}
        subtitulo={confirmacion?.item?.concepto || (confirmacion?.tipo === "eliminar_retiro" ? "Retiro" : "")}
        confirmar={{
          label: confirmacion?.tipo === "eliminar_retiro" ? "Eliminar retiro" : "Eliminar gasto",
          savingLabel: "Eliminando…",
          tono: "rojo",
          onClick: ejecutarConfirmacion,
        }}
      >
        {confirmacion?.tipo === "eliminar_retiro" ? (
          <>
            <p>
              El retiro desaparece de Movimientos y el saldo de la cuenta sube en
              ese monto. Esta acción no se puede deshacer.
            </p>
            <Resumen>
              <Fila tono="rojo" monto={formatMXN(confirmacion.item?.monto)}>
                {confirmacion.item?.concepto || "Retiro"}
              </Fila>
              <Fila tono="zinc">
                {formatCalendarDate(confirmacion.item?.fecha, calendarTimezone, { style: "short" }) || "—"}
                {confirmacion.item?.metodo ? ` · ${confirmacion.item.metodo}` : ""}
              </Fila>
            </Resumen>
          </>
        ) : confirmacion ? (
          <>
            <p>
              El gasto desaparece de Movimientos, del neto y de los reportes. Esta
              acción no se puede deshacer.
            </p>
            <Resumen>
              <Fila tono="rojo" monto={formatMXN(confirmacion.item?.monto)}>
                {confirmacion.item?.concepto || "Gasto"}
              </Fila>
              <Fila tono="zinc">
                {formatCalendarDate(confirmacion.item?.fechaPago || confirmacion.item?.fecha, calendarTimezone, { style: "short" }) || "—"}
                {confirmacion.item?.categoria ? ` · ${confirmacion.item.categoria}` : ""}
                {confirmacion.item?.metodo ? ` · ${confirmacion.item.metodo}` : ""}
              </Fila>
            </Resumen>
          </>
        ) : null}
      </DialogoConfirmacion>

      {/* Gastos recurrentes que tocan confirmar (FRA-356): sin confirmar, no
          existen para el neto */}
      <PropuestasRecurrentes />

      {/* Encabezado: saldo y comprometido como dos cifras distintas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Saldo de caja
          </div>
          <div className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">
            {formatMXN(encabezado.saldo)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Todo lo que ha entrado menos lo que ha salido
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 p-4">
          <div className="text-xs uppercase tracking-wide font-semibold text-amber-700">
            Comprometido en depósitos
          </div>
          <div className="mt-1 text-2xl font-bold text-amber-700">
            {formatMXN(encabezado.comprometido)}
          </div>
          <div className="text-xs text-amber-700/70 mt-1">
            Está en la cuenta, pero se debe
          </div>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4">
          <div className="text-xs uppercase tracking-wide font-semibold text-zinc-500">
            Tuyo
          </div>
          <div
            className={`mt-1 text-2xl font-bold ${
              encabezado.propio >= 0 ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {formatMXN(encabezado.propio)}
          </div>
          <div className="text-xs text-zinc-500 mt-1">Saldo menos lo comprometido</div>
        </div>
      </div>

      {/* Controles del período */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={vista}
          onChange={(e) => updateParams({ vista: e.target.value })}
          className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm"
        >
          <option value="mensual">Mensual</option>
          <option value="anual">Anual</option>
        </select>
        <select
          value={selectedYear}
          onChange={(e) =>
            updateParams({
              month: `${e.target.value}-${String(selectedMonthNum).padStart(2, "0")}`,
            })
          }
          className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm"
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {vista === "mensual" && (
          <select
            value={selectedMonthNum}
            onChange={(e) =>
              updateParams({
                month: `${selectedYear}-${String(e.target.value).padStart(2, "0")}`,
              })
            }
            className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-sm"
          >
            {MONTHS_ES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm">
          <span className="text-zinc-500">
            Ingresos:{" "}
            <span className="font-semibold text-emerald-600">{formatMXN(totalIngresos)}</span>
          </span>
          <span className="text-zinc-500">
            Egresos:{" "}
            <span className="font-semibold text-red-600">{formatMXN(totalEgresos)}</span>
          </span>
          <span className="text-zinc-500">
            Neto del período:{" "}
            <span className={`font-semibold ${neto >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {formatMXN(neto)}
            </span>
          </span>
          {!soloLectura && (
          <>
          <button
            type="button"
            onClick={() => {
              if (showForm) cerrarFormGasto();
              else {
                setEditandoGasto(null);
                setForm(emptyGasto);
                setShowForm(true);
              }
              setShowRetiro(false);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            Gasto general
          </button>
          <button
            type="button"
            onClick={() => { setShowRetiro((v) => !v); cerrarFormGasto(); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-800 transition hover:bg-zinc-50 dark:hover:bg-zinc-700"
            title="Dinero que sacas del negocio: baja el saldo sin ser gasto"
          >
            <PlusIcon className="h-4 w-4" />
            Retiro
          </button>
          </>
          )}
        </div>
      </div>

      {/* Captura de retiro — su única superficie */}
      {showRetiro && !soloLectura && (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/30 p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1">
            Registrar retiro
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Dinero que sacas del negocio. Baja el saldo de caja sin contar como
            gasto: no toca la utilidad de tus eventos ni ningún reporte de
            resultados.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Monto</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmtMXNInput(retiroForm.monto)}
                  onChange={(e) => setRetiroForm({ ...retiroForm, monto: normalizeMoneyInput(e.target.value) })}
                  placeholder="0"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha</label>
              <input
                type="date"
                value={retiroForm.fecha}
                onChange={(e) => setRetiroForm({ ...retiroForm, fecha: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Concepto (opcional)</label>
              <input
                type="text"
                value={retiroForm.concepto}
                onChange={(e) => setRetiroForm({ ...retiroForm, concepto: e.target.value })}
                placeholder="Utilidades de septiembre"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Método (opcional)</label>
              <MetodoPagoField
                value={retiroForm.metodo}
                onChange={(v) => setRetiroForm({ ...retiroForm, metodo: v })}
                metodosPago={metodosPago}
                className={inputClass}
                placeholder="Transferencia"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleAddRetiro}
              disabled={savingRetiro}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {savingRetiro ? "Guardando…" : "Registrar retiro"}
            </button>
            <button
              type="button"
              onClick={() => { setShowRetiro(false); setRetiroForm(emptyRetiro); }}
              className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Captura de gasto general — su única superficie */}
      {showForm && !soloLectura && (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/30 p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
            {editandoGasto ? "Editar gasto general del venue" : "Registrar gasto general del venue"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-zinc-500 mb-1">Concepto</label>
              <input
                type="text"
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="Renta del mes, nómina, mantenimiento…"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Monto</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmtMXNInput(form.monto)}
                  onChange={(e) => setForm({ ...form, monto: normalizeMoneyInput(e.target.value) })}
                  placeholder="0"
                  className={`${inputClass} pl-7`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                {editandoGasto && form.estado === "pagado" ? "Fecha de pago" : "Fecha"}
              </label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Categoría</label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
              >
                {CATEGORIAS_COSTO.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {(proveedoresActivos.length > 0 || recursosPropios.length > 0) && (
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  Proveedor o servicio (opcional)
                </label>
                <select
                  value={form.respaldo}
                  onChange={(e) => setForm({ ...form, respaldo: e.target.value })}
                  className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm max-w-[14rem]"
                  title="A qué proveedor o recurso pertenece este gasto. Un mantenimiento ligado a un recurso cuenta en la recuperación de su inversión."
                >
                  <option value="">Nada en particular</option>
                  {proveedoresActivos.length > 0 && (
                    <optgroup label="Proveedor">
                      {proveedoresActivos.map((p) => (
                        <option key={p.id} value={`proveedor:${p.id}`}>{p.nombre}</option>
                      ))}
                    </optgroup>
                  )}
                  {recursosPropios.length > 0 && (
                    <optgroup label="Recurso propio">
                      {recursosPropios.map((r) => (
                        <option key={r.id} value={`recurso:${r.id}`}>{r.nombre}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            )}
            <div className="flex rounded-lg border border-zinc-300 dark:border-zinc-600 overflow-hidden self-end">
              {[
                { id: "pagado", label: "Pagado" },
                { id: "por_pagar", label: "Por pagar" },
              ].map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setForm({ ...form, estado: e.id })}
                  className={`px-3 py-2 text-sm font-medium transition ${
                    form.estado === e.id
                      ? "text-white"
                      : "bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50"
                  }`}
                  style={form.estado === e.id ? { backgroundColor: ACCENT } : undefined}
                >
                  {e.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setMasDetalles((v) => !v)}
              className="self-end mb-2 text-xs font-medium text-zinc-500 hover:text-zinc-700"
            >
              {masDetalles ? "− Menos detalles" : "+ Más detalles"}
            </button>
          </div>

          {masDetalles && (
            <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700/40 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Método</label>
                <MetodoPagoField
                  value={form.metodo}
                  onChange={(v) => setForm({ ...form, metodo: v })}
                  metodosPago={metodosPago}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Notas</label>
                <input
                  type="text"
                  value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleAddGasto}
              disabled={saving}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {saving ? "Guardando…" : editandoGasto ? "Guardar cambios" : "Registrar gasto"}
            </button>
            <button
              type="button"
              onClick={cerrarFormGasto}
              className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Catálogo de gastos recurrentes */}
      <CuentasRecurrentes />

      {/* Ingresos */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
            Ingresos
          </h3>
          <span className="text-sm font-semibold text-emerald-600">
            {formatMXN(totalIngresos)}
          </span>
        </div>
        {ingresos.length === 0 ? (
          <p className="text-sm text-zinc-400 py-4 text-center rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700/60">
            Sin ingresos en este período.
          </p>
        ) : (
          <Tabla lista={ingresos} />
        )}
      </div>

      {/* Egresos */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
            Egresos
          </h3>
          <span className="text-sm font-semibold text-red-600">
            {formatMXN(totalEgresos)}
          </span>
        </div>
        {egresos.length === 0 ? (
          <p className="text-sm text-zinc-400 py-4 text-center rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700/60">
            Sin egresos en este período.
          </p>
        ) : (
          <Tabla lista={egresos} egreso />
        )}
      </div>

      <p className="mt-4 text-xs text-zinc-400">
        Cada egreso cuenta con la fecha en que salió el dinero. Los costos de un
        evento se corrigen desde el evento; aquí se editan los gastos generales
        y se eliminan los gastos generales y los retiros. Lo que debes y
        no has pagado vive en Por pagar; los depósitos en garantía no entran al
        neto — no son tuyos — pero sí al saldo y a lo comprometido de arriba.
        Los retiros bajan el saldo sin ser gasto. Lo que compras o repones
        cuenta como egreso el día que lo pagas. El neto de aquí incluye los
        retiros y deja fuera los depósitos, así que no es el mismo número que
        el “Neto de operación” del Resumen ni el “Flujo neto” de Flujo de caja.
      </p>
    </div>
  );
};

export default CuentasBalance;
